import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import authService from '../api/authService';
// Paleta institucional Elecom — tokens compartidos (ver src/theme/colors.js)
import { COLORS as C } from '../theme/colors';

/**
 * @description Valida los campos del formulario de registro de forma local,
 * antes de enviar la petición al backend.
 * @param {object} fields - Campos del formulario.
 * @returns {object} Mapa de campo → mensaje de error. Vacío si no hay errores.
 */
const validateFields = ({ username, phone, email, password }) => {
  const errors = {};

  if (!username.trim()) {
    errors.username = 'El nombre completo es requerido.';
  }

  if (!phone.trim()) {
    errors.phone = 'El teléfono es requerido.';
  } else if (!/^\+?[\d\s\-()]{7,20}$/.test(phone.trim())) {
    errors.phone = 'Ingrese un número de teléfono válido.';
  }

  if (!email.trim()) {
    errors.email = 'El correo electrónico es requerido.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
    errors.email = 'Ingrese un correo electrónico válido.';
  }

  if (!password) {
    errors.password = 'La contraseña es requerida.';
  } else if (password.length < 6) {
    errors.password = 'La contraseña debe tener al menos 6 caracteres.';
  }

  return errors;
};

/**
 * @description Página de registro de nuevos técnicos.
 * Crea cuentas con rol TECHNICIAN. Los roles ADMIN y SUPERVISOR
 * se asignan desde el panel de administración de usuarios.
 * Tras el registro exitoso redirige a /login para que el usuario ingrese.
 */
const Register = () => {
  const [username, setUsername] = useState('');
  const [phone, setPhone]       = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);

  // Errores de validación por campo
  const [fieldErrors, setFieldErrors] = useState({});
  // Error general de la API (p.ej. email duplicado)
  const [apiError, setApiError]       = useState('');
  const [loading, setLoading]         = useState(false);
  // Mensaje de éxito breve antes de redirigir
  const [success, setSuccess]         = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    setSuccess(false);

    // Validación local antes de llamar a la API
    const errors = validateFields({ username, phone, email, password });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setLoading(true);

    try {
      await authService.register({ username, phone, email, password });
      setSuccess(true);
      // Redirige a la gestión de usuarios tras confirmar el registro exitoso
      setTimeout(() => navigate('/users'), 1500);
    } catch (err) {
      // El backend retorna { message: string | string[] } en caso de error
      const raw = err?.message;
      const message = Array.isArray(raw)
        ? raw.join(' ')
        : raw || 'No se pudo crear la cuenta. Intente nuevamente.';
      setApiError(message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * @description Limpia el error de un campo cuando el usuario comienza a editarlo,
   * para evitar que los mensajes persistan innecesariamente.
   */
  const clearFieldError = (field) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* Logo */}
        <img src="/logo.png" alt="Elecom Elevator Components" style={styles.logo} />

        {/* Separador */}
        <div style={styles.divider} />

        {/* Título */}
        <h1 style={styles.title}>Crear usuario</h1>
        <p style={styles.subtitle}>Complete los datos para registrar un nuevo usuario en el sistema</p>

        {/* Formulario */}
        <form onSubmit={handleSubmit} style={styles.form} noValidate>

          {/* Nombre completo */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="username">Nombre completo</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>👤</span>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => { setUsername(e.target.value); clearFieldError('username'); }}
                placeholder="Juan Pérez"
                autoComplete="name"
                style={fieldErrors.username ? styles.inputError : styles.input}
                onFocus={(e) => Object.assign(e.target.style, fieldErrors.username ? styles.inputErrorFocus : styles.inputFocus)}
                onBlur={(e)  => Object.assign(e.target.style, fieldErrors.username ? styles.inputError  : styles.input)}
              />
            </div>
            {fieldErrors.username && (
              <span style={styles.fieldError} role="alert">{fieldErrors.username}</span>
            )}
          </div>

          {/* Teléfono */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="phone">Teléfono</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>📞</span>
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); clearFieldError('phone'); }}
                placeholder="+57 300 000 0000"
                autoComplete="tel"
                style={fieldErrors.phone ? styles.inputError : styles.input}
                onFocus={(e) => Object.assign(e.target.style, fieldErrors.phone ? styles.inputErrorFocus : styles.inputFocus)}
                onBlur={(e)  => Object.assign(e.target.style, fieldErrors.phone ? styles.inputError  : styles.input)}
              />
            </div>
            {fieldErrors.phone && (
              <span style={styles.fieldError} role="alert">{fieldErrors.phone}</span>
            )}
          </div>

          {/* Email */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="email">Correo electrónico</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>✉</span>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearFieldError('email'); }}
                placeholder="usuario@empresa.com"
                autoComplete="email"
                style={fieldErrors.email ? styles.inputError : styles.input}
                onFocus={(e) => Object.assign(e.target.style, fieldErrors.email ? styles.inputErrorFocus : styles.inputFocus)}
                onBlur={(e)  => Object.assign(e.target.style, fieldErrors.email ? styles.inputError  : styles.input)}
              />
            </div>
            {fieldErrors.email && (
              <span style={styles.fieldError} role="alert">{fieldErrors.email}</span>
            )}
          </div>

          {/* Contraseña */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="password">Contraseña</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>🔒</span>
              <input
                id="password"
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearFieldError('password'); }}
                placeholder="••••••••"
                autoComplete="new-password"
                style={fieldErrors.password
                  ? { ...styles.inputError, paddingRight: '40px' }
                  : { ...styles.input,      paddingRight: '40px' }
                }
                onFocus={(e) => Object.assign(e.target.style,
                  fieldErrors.password
                    ? { ...styles.inputErrorFocus, paddingRight: '40px' }
                    : { ...styles.inputFocus,      paddingRight: '40px' }
                )}
                onBlur={(e) => Object.assign(e.target.style,
                  fieldErrors.password
                    ? { ...styles.inputError, paddingRight: '40px' }
                    : { ...styles.input,      paddingRight: '40px' }
                )}
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                style={styles.toggleBtn}
                tabIndex={-1}
                aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPass ? '🙈' : '👁'}
              </button>
            </div>
            {fieldErrors.password && (
              <span style={styles.fieldError} role="alert">{fieldErrors.password}</span>
            )}
          </div>

          {/* Error de la API */}
          {apiError && (
            <div style={styles.errorBox} role="alert">
              {apiError}
            </div>
          )}

          {/* Confirmación de éxito */}
          {success && (
            <div style={styles.successBox} role="status">
              Usuario creado exitosamente. Redirigiendo a la gestión de usuarios...
            </div>
          )}

          {/* Botón de registro */}
          <button type="submit" disabled={loading || success} style={styles.submitBtn}>
            {loading
              ? <span style={styles.spinner} />
              : 'Crear cuenta'
            }
          </button>
        </form>

        {/* Enlace de retorno a la gestión de usuarios */}
        <p style={styles.loginLink}>
          <Link to="/users" style={styles.link}>← Volver a usuarios</Link>
        </p>

        {/* Pie */}
        <p style={styles.footer}>Elecom Elevator Components</p>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: C.pageBg,
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    padding: '24px',
    boxSizing: 'border-box',
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    backgroundColor: C.white,
    borderRadius: '14px',
    padding: '40px 36px 32px',
    boxShadow: '0 4px 24px rgba(56,62,65,0.1)',
    border: '1px solid rgba(56,62,65,0.08)',
    boxSizing: 'border-box',
  },
  logo: {
    display: 'block',
    margin: '0 auto 16px',
    height: '52px',
    objectFit: 'contain',
  },
  divider: {
    width: '40px',
    height: '2px',
    backgroundColor: 'rgba(56,62,65,0.15)',
    borderRadius: '1px',
    margin: '0 auto 24px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: C.teal,
    textAlign: 'center',
    margin: '0 0 6px',
    letterSpacing: '0.3px',
  },
  subtitle: {
    fontSize: '13px',
    color: 'rgba(56,62,65,0.5)',
    textAlign: 'center',
    margin: '0 0 28px',
    lineHeight: '1.5',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    fontSize: '12px',
    fontWeight: '600',
    color: 'rgba(56,62,65,0.6)',
    letterSpacing: '0.3px',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: '12px',
    fontSize: '14px',
    pointerEvents: 'none',
    opacity: 0.5,
  },
  // Estado normal
  input: {
    width: '100%',
    padding: '10px 12px 10px 36px',
    fontSize: '14px',
    color: C.charcoal,
    backgroundColor: C.white,
    border: '1px solid rgba(56,62,65,0.2)',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  // Estado con foco (sin error)
  inputFocus: {
    width: '100%',
    padding: '10px 12px 10px 36px',
    fontSize: '14px',
    color: C.charcoal,
    backgroundColor: C.white,
    border: `1.5px solid ${C.teal}`,
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  // Estado con error de validación
  inputError: {
    width: '100%',
    padding: '10px 12px 10px 36px',
    fontSize: '14px',
    color: C.charcoal,
    backgroundColor: C.white,
    border: '1.5px solid #f87171',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  // Estado con foco + error
  inputErrorFocus: {
    width: '100%',
    padding: '10px 12px 10px 36px',
    fontSize: '14px',
    color: C.charcoal,
    backgroundColor: C.white,
    border: '2px solid #ef4444',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  toggleBtn: {
    position: 'absolute',
    right: '10px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '4px',
    opacity: 0.55,
    lineHeight: 1,
  },
  // Mensaje de error por campo (debajo del input)
  fieldError: {
    fontSize: '11px',
    color: '#b91c1c',
    marginTop: '2px',
    lineHeight: '1.4',
  },
  // Caja de error general de la API
  errorBox: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fca5a5',
    color: '#b91c1c',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    lineHeight: '1.4',
  },
  // Caja de confirmación de registro exitoso
  successBox: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #86efac',
    color: '#166534',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    lineHeight: '1.4',
  },
  submitBtn: {
    width: '100%',
    height: '46px',
    backgroundColor: C.teal,
    color: C.white,
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    letterSpacing: '0.3px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '4px',
    opacity: 1,
    transition: 'opacity 0.15s',
  },
  spinner: {
    display: 'inline-block',
    width: '18px',
    height: '18px',
    border: '2.5px solid rgba(255,255,255,0.35)',
    borderTop: '2.5px solid #fff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loginLink: {
    textAlign: 'center',
    fontSize: '13px',
    color: 'rgba(56,62,65,0.55)',
    marginTop: '20px',
    marginBottom: '0',
  },
  link: {
    color: C.teal,
    fontWeight: '600',
    textDecoration: 'none',
  },
  footer: {
    textAlign: 'center',
    fontSize: '11px',
    color: 'rgba(56,62,65,0.3)',
    letterSpacing: '0.8px',
    marginTop: '20px',
    marginBottom: '0',
  },
};

export default Register;
